import { create } from '@bufbuild/protobuf'
import { BidSegment } from '@ebalance/proto/bfftypes/v1/bid_segment_pb'
import type { FeeAutoConsiderPolicy } from '@ebalance/proto/bffuser/v1/bid_limit_model_pb'
import { UpdateFeeAutoConsiderPolicyRequestSchema } from '@ebalance/proto/bffuser/v1/bid_limit_service_pb'
import type { Decimal } from '@ebalance/proto/google/type/decimal_pb'
import { useTranslation } from 'react-i18next'
import { Link as RouterLink } from 'react-router'

import {
  Alert,
  Box,
  Flex,
  Link,
  Switch,
  Text,
  type ToastType,
  toaster,
} from '@enechain/ecloud-designsystem'

import { ButtonTooltip } from '~/components/common/ButtonTooltip'
import { useWorkflowLockReasons } from '~/hooks/workflowCase/useWorkflowLockReasons'
import {
  computeFeeAdjustedPriceBoundsExample,
  formatFeePerKwh,
} from '~/models/jepxFee'
import { useUpdateFeeAutoConsiderPolicy } from '~/pages/AdminBidLimits/hooks/useBidLimitSettings'
import { FIRST_SAVE_EXPECTED_UPDATED_AT } from '~/pages/AdminBidLimits/models/constants'
import { routePaths } from '~/routes/routePaths'

// chakra v3 / ark-ui の d.ts 解決問題 (worktree で @ark-ui/react 未 install) により
// toaster が `CreateToasterReturn` → `any` に collapse する。 MarketOrderPolicyCard
// と同じ cast で型を復元する。
type ToasterCreate = {
  create: (options: { type?: ToastType; title?: string }) => unknown
}
const typedToaster = toaster as ToasterCreate

type Props = {
  policy: FeeAutoConsiderPolicy | undefined
  segment: BidSegment.SPOT_NORMAL | BidSegment.INTRADAY
  /**
   * JEPX 会員情報 (AC-IJM-007a が SoT) から取得した現在の手数料単価
   * (`useFeeUnitPrices`)。 取得中・取得失敗・会員 ID 未登録・未設定は undefined で、
   * そのときは単価の数値を出さず「単価を表示できない」旨の文言に切り替える。
   */
  feeUnitPrice: Decimal | undefined
  /**
   * workflow-back-bar-readonly AC-17: ワークフローケースが完了済み/削除済みのとき true。
   * true のときトグルを disabled にする。
   */
  isReadonly?: boolean
}

/**
 * 「手数料自動考慮」カード (AC-BDL-015/017 スポット、AC-BDL-018/020 時間前)。
 *
 * 成行注文許可カード (`MarketOrderPolicyCard`) と同じ作法: 単発トグルの即時保存 +
 * 保存成功/失敗 toast。 エリア次元が無い単一 boolean のため楽観更新の対象は
 * トグル自体のみ。
 *
 * OFF 時に限らず常に現在の手数料単価と JEPX 会員情報管理画面への案内を表示する
 * (design `admin/bid-limits.html` 準拠。 AC-BDL-017/020 は「WHILE OFF」の要求だが
 * ON 時も表示して害はなく、design 側も条件分岐していない)。
 */
export const FeeAutoConsiderPolicyCard = ({
  policy,
  segment,
  feeUnitPrice,
  isReadonly = false,
}: Props): React.ReactElement => {
  const { t } = useTranslation()
  const { readonlyReason } = useWorkflowLockReasons()
  const { mutateAsync, isPending } = useUpdateFeeAutoConsiderPolicy()

  const isIntraday = segment === BidSegment.INTRADAY
  const enabled = policy?.enabled ?? false
  const feeText =
    feeUnitPrice !== undefined ? formatFeePerKwh(feeUnitPrice) : undefined
  const priceBoundsExample = computeFeeAdjustedPriceBoundsExample(feeUnitPrice)

  const handleToggle = async (newEnabled: boolean): Promise<void> => {
    try {
      await mutateAsync(
        create(UpdateFeeAutoConsiderPolicyRequestSchema, {
          bidSegment: segment,
          enabled: newEnabled,
          // 初回保存 (policy 未取得) は epoch 番兵を送る (= backend が None 扱い)。
          expectedUpdatedAt:
            policy?.updatedAt ?? FIRST_SAVE_EXPECTED_UPDATED_AT,
        }),
      )
      // MarketOrderPolicyCard と同じ理由 (2026-09-02 デザイナー指摘): 即時保存の
      // トグルは保存成功を伝える手掛かりが画面上に無いため、成功 toast を出す。
      typedToaster.create({
        type: 'success',
        title: t('page:adminBidLimits.feeAutoConsider.saveSuccess'),
      })
    } catch {
      typedToaster.create({
        type: 'error',
        title: t('page:adminBidLimits.feeAutoConsider.saveError'),
      })
    }
  }

  const isDisabled = isPending || isReadonly
  const disabledReason =
    readonlyReason ??
    (isPending ? t('page:adminBidLimits.savingTooltip') : undefined)

  return (
    <Box>
      <Text marginBottom={3} color="Text.midEmphasis" fontSize="md">
        {t('page:adminBidLimits.feeAutoConsider.description')}
      </Text>
      {/* [DS制約] MarketOrderPolicyCard と同じ理由で Switch の children ラベル
          スロットは使わず、aria-label + 兄弟 Text で表示ラベルを出す。
          無効時の理由は包みの DS Tooltip で出す (UI 辞書 action-reason.md の区分 2。
          native の title は使わない)。 */}
      <Flex alignItems="center" gap="8px">
        <ButtonTooltip content={disabledReason}>
          <Switch
            aria-label={t('page:adminBidLimits.feeAutoConsider.label')}
            checked={enabled}
            disabled={isDisabled}
            onCheckedChange={(details: { checked: boolean }) => {
              void handleToggle(details.checked)
            }}
          />
        </ButtonTooltip>
        <Text fontSize="md">
          {t('page:adminBidLimits.feeAutoConsider.label')}
        </Text>
      </Flex>
      <Text marginTop={2} color="Text.midEmphasis" fontSize="sm">
        {!enabled
          ? t('page:adminBidLimits.feeAutoConsider.summaryOff')
          : feeText !== undefined
            ? t('page:adminBidLimits.feeAutoConsider.summaryOn', {
                amount: feeText,
              })
            : t('page:adminBidLimits.feeAutoConsider.summaryOnNoFee')}
      </Text>
      {/* AC-BDL-017 / AC-BDL-020: 現在の手数料単価と JEPX 会員情報管理画面への
          案内リンク。 単価そのものはこの画面では編集しない (会員情報管理が SoT)。 */}
      <Text marginTop={2} color="Text.lowEmphasis" fontSize="sm">
        {feeText !== undefined
          ? t(
              isIntraday
                ? 'page:adminBidLimits.feeAutoConsider.unitPriceHintPrefixIntraday'
                : 'page:adminBidLimits.feeAutoConsider.unitPriceHintPrefixSpot',
              { amount: feeText },
            )
          : t(
              isIntraday
                ? 'page:adminBidLimits.feeAutoConsider.unitPriceHintPrefixIntradayNoFee'
                : 'page:adminBidLimits.feeAutoConsider.unitPriceHintPrefixSpotNoFee',
            )}
        <Link asChild>
          <RouterLink to={routePaths.adminJepxMemberInfo.path}>
            {t('page:adminBidLimits.feeAutoConsider.unitPriceHintLinkLabel')}
          </RouterLink>
        </Link>
        {t('page:adminBidLimits.feeAutoConsider.unitPriceHintSuffix')}
      </Text>
      {/* AC-BDL-021: 「手数料自動考慮」の設定近傍に、 ON / OFF に関わらず常時表示する
          注釈 (表示位置・体裁はデザイナー裁量、 spec 準拠で本 Alert に集約)。 */}
      <Alert
        marginTop={3}
        borderRadius="md"
        description={
          <Box>
            <Text>
              {t('page:adminBidLimits.feeAutoConsider.annotationLine2')}
            </Text>
            <Text marginTop={1}>
              {t('page:adminBidLimits.feeAutoConsider.annotationLine3')}
            </Text>
            <Text marginTop={1}>
              {priceBoundsExample !== undefined
                ? t('page:adminBidLimits.feeAutoConsider.annotationExample', {
                    fee: feeText,
                    minBuy: priceBoundsExample.minBuyPrice,
                    maxSell: priceBoundsExample.maxSellPrice,
                  })
                : t(
                    'page:adminBidLimits.feeAutoConsider.annotationExampleNoFee',
                  )}
            </Text>
            <Text marginTop={1}>
              {t('page:adminBidLimits.feeAutoConsider.annotationLine5')}
            </Text>
          </Box>
        }
        status="info"
        title={t('page:adminBidLimits.feeAutoConsider.annotationLine1')}
      />
    </Box>
  )
}
